import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Setting, INITIAL_SETTING_STATE} from "../contexts/setting";
import {Edits, INITIAL_EDITS_STATE} from "../contexts/edits";
import {Images, INITIAL_IMAGES_STATE} from "../contexts/images";
import {bannerToLegacyScene} from "./legacy";
import {createBannerWriters, createLayoutPatch, imageSlotForKey, type BannerChange, type BannerUpdate} from "./scope-adapter";
import {uploadBannerAsset} from "./asset-upload";
import {LatestOperation} from "../libs/latest-operation";
import {BannerRuntime, type AssetTarget} from "./BannerRuntime";
import {type BannerInstance, type BannerPatch} from "./document";
import {getTemplateDefinition} from "../data/template-catalog";
import {getSizeFromBase64} from "../libs/bg-utils";
import {coverFitTransform} from "../libs/utils";
import {useQueryClient} from "@tanstack/react-query";
import {createGridResources} from "./resources";
import {resolveBannerVisualPatch} from "./background";

type Props = {key?: string; banner: BannerInstance; onChange: BannerChange; renderMode?: boolean; onPendingChange?: (instanceId: string, count: number) => void; children: React.ReactNode};
type VisualSelection = {layout: number; background: string};
type PendingVisualSelection = VisualSelection & {kind: "preset" | "upload"; edits: BannerUpdate[]};

function preserveLaterVisualEdits(patch: BannerPatch, edits: BannerUpdate[]): BannerUpdate {
  const laterEdits = edits.slice();
  return (banner) => {
    const appearance = {...patch.appearance}, assets = {...patch.assets};
    for (const edit of laterEdits) {
      const changes = typeof edit === "function" ? edit(banner) : edit;
      for (const key of Object.keys(changes.appearance ?? {}) as (keyof typeof appearance)[]) delete appearance[key];
      for (const key of Object.keys(changes.assets ?? {}) as (keyof typeof assets)[]) delete assets[key];
    }
    return {...patch, appearance, assets};
  };
}

// The document owns persisted values. Tools, upload previews and progress live only here.
export function BannerScope({banner, onChange, renderMode = false, onPendingChange, children}: Props) {
  const current = useRef(banner);
  current.current = banner;
  const operations = useRef(new Map<string, LatestOperation>());
  const alive = useRef(true);
  useEffect(() => {alive.current = true; return () => {alive.current = false; operations.current.forEach((operation) => operation.dispose());};}, []);
  const [tools, setTools] = useState({background: false, image: false, imageId: null as string | null, dropzones: true, typograf: false, paused: renderMode});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(0);
  const [visualSelection, setVisualSelection] = useState<VisualSelection | null>(null);
  const intendedSelection = useRef<PendingVisualSelection | null>(null);
  const client = useQueryClient();
  const resources = useMemo(() => createGridResources(client), [client]);
  const [uploadError, setUploadError] = useState("");
  useEffect(() => {
    onPendingChange?.(banner.instanceId, pending + (visualSelection === null || visualSelection.background === "custom" ? 0 : 1));
    return () => onPendingChange?.(banner.instanceId, 0);
  }, [banner.instanceId, pending, visualSelection, onPendingChange]);
  const [versions, setVersions] = useState<Record<string, number>>({});
  const [animation, setAnimation] = useState({background: false, upload: false});
  const frameReader = useRef<(() => unknown) | null>(null);
  const registerGenerativeFrameReader = useCallback((reader: () => unknown) => {
    if (typeof reader !== "function") throw new Error("Generative frame reader must be a function");
    frameReader.current = reader;
    return () => {if (frameReader.current === reader) frameReader.current = null;};
  }, []);
  const write = useCallback((update: BannerUpdate) => {
    if (!alive.current) return;
    intendedSelection.current?.edits.push(update);
    onChange(current.current.instanceId, update);
  }, [onChange]);
  const writers = useMemo(() => createBannerWriters(() => current.current, write), [write]);
  const cancelUpload = useCallback((target: AssetTarget, preserveLayout = true) => {
    operations.current.get(target)?.cancel();
    setUploadError("");
    if (target === "background") {
      const layout = intendedSelection.current?.layout;
      intendedSelection.current = null; setVisualSelection(null);
      // A replacement background keeps the user's layout choice.
      if (preserveLayout && layout !== undefined) writers.editLayoutOption(layout);
    }
    const key = target === "background" || target === "logo" ? target : `image:${target === "image" ? "main" : target}`;
    setPreviews((state) => {const next = {...state}; delete next[key]; return next;});
  }, [writers]);
  // Background operations belong to the banner, so changing the sidebar selection cannot cancel a pick.
  // Uploads and presets share a revision: only the latest background choice may commit.
  const selectVisuals = useCallback(async (layout: number, background: string) => {
    cancelUpload("background", false);
    let operation = operations.current.get("background");
    if (!operation) {operation = new LatestOperation(); operations.current.set("background", operation);}
    const isCurrent = operation.start();
    const selection: PendingVisualSelection = {layout, background, kind: "preset", edits: []};
    intendedSelection.current = selection;
    setVisualSelection({layout, background}); setUploadError("");
    try {
      const patch = await resolveBannerVisualPatch(current.current, {appearance: {...createLayoutPatch(current.current, layout).appearance, backgroundOption: background}}, resources);
      if (!alive.current || !isCurrent()) return false;
      onChange(current.current.instanceId, preserveLaterVisualEdits(patch, selection.edits));
      return true;
    } catch (error) {
      if (alive.current && isCurrent()) setUploadError(error instanceof Error ? error.message : "Could not load background");
      return false;
    } finally {
      if (alive.current && isCurrent()) {intendedSelection.current = null; setVisualSelection(null);}
    }
  }, [cancelUpload, resources, onChange]);
  const selectBackground = useCallback((option: string) => selectVisuals(intendedSelection.current?.layout ?? current.current.appearance.layout, option), [selectVisuals]);
  const selectLayout = useCallback((layout: number) => {
    const selection = intendedSelection.current;
    if (selection?.kind === "upload") {
      selection.layout = layout;
      setVisualSelection({layout, background: selection.background});
      writers.editLayoutOption(layout);
      return;
    }
    const background = intendedSelection.current?.background ?? current.current.appearance.backgroundOption;
    // A layout change must not cancel an upload or replace a custom/generated background.
    if (!intendedSelection.current && (previews.background || background === "custom" || background === "generative")) {
      writers.editLayoutOption(layout);
      return;
    }
    void selectVisuals(layout, background);
  }, [previews.background, selectVisuals, writers]);
  const upload = useCallback(async (target: AssetTarget, file: File) => {
    const selection: PendingVisualSelection | null = target === "background" ? {
      layout: intendedSelection.current?.layout ?? current.current.appearance.layout, background: "custom", kind: "upload", edits: [],
    } : null;
    if (selection) {
      cancelUpload("background", false);
      intendedSelection.current = selection; setVisualSelection({layout: selection.layout, background: selection.background});
    }
    let operation = operations.current.get(target);
    if (!operation) {operation = new LatestOperation(); operations.current.set(target, operation);}
    const isCurrent = operation.start();
    const previewKey = target === "background" || target === "logo" ? target : `image:${target === "image" ? "main" : target}`;
    const localUrl = URL.createObjectURL(file);
    setPreviews((state) => ({...state, [previewKey]: localUrl}));
    setPending((n) => n + 1);
    setUploadError("");
    setTools((t) => ({...t, background: target === "background", image: target !== "background", imageId: target === "image" || target === "logo" ? current.current.instanceId : /^(speaker-|ls-speaker-|custom-qr-)/.test(target) ? `${current.current.instanceId}-${target}` : `${target}-${current.current.instanceId}`}));
    if (target !== "background" && target !== "logo") {
      const slot = target === "image" ? "main" : target;
      setVersions((state) => ({...state, [slot]: (state[slot] ?? 0) + 1}));
    }
    try {
      const url = await uploadBannerAsset(file);
      if (!alive.current || !isCurrent()) return false;
      if (selection) {
        const size = await getSizeFromBase64(file.type.startsWith("video/") ? url : localUrl) as {width: number; height: number};
        if (!Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width <= 0 || size.height <= 0) throw new Error("Cannot read background dimensions");
        if (!alive.current || !isCurrent()) return false;
        const definition = getTemplateDefinition(current.current.templateKey);
        const fit = coverFitTransform(size.width, size.height, definition.width, definition.height);
        const patch: BannerPatch = {assets: {background: url}, appearance: {...createLayoutPatch(current.current, selection.layout).appearance, backgroundOption: "custom", backgroundX: fit.x, backgroundY: fit.y, backgroundScale: fit.scale, backgroundRotate: 0, backgroundFlip: {x: false, y: false}}};
        onChange(current.current.instanceId, preserveLaterVisualEdits(patch, selection.edits));
      }
      else if (target === "logo") writers.editLogos(current.current.instanceId, url);
      else writers.editUploadImages(target === "image" ? current.current.instanceId : target === "overlay" ? `overlay-${current.current.instanceId}` : target, url);
      return true;
    } catch (error) {
      if (alive.current && isCurrent()) setUploadError(error instanceof Error ? error.message : "Asset upload failed");
      return false;
    } finally {
      URL.revokeObjectURL(localUrl);
      if (alive.current) {
        if (isCurrent()) {
          if (selection) {intendedSelection.current = null; setVisualSelection(null);}
          setPreviews((state) => {const next = {...state}; delete next[previewKey]; return next;});
        }
        setPending((n) => Math.max(0, n - 1));
      }
    }
  }, [writers, cancelUpload, onChange]);
  const imageWriters = useMemo(() => {
    const assetWriter = (name: string, valueIndex: number, previewKey: (args: unknown[]) => string) => (...args: unknown[]) => {
      if (!alive.current) return;
      const value = args[valueIndex];
      const key = previewKey(args);
      if (typeof value === "string" && value.startsWith("blob:")) {
        setPreviews((state) => ({...state, [key]: value}));
      } else {
        writers[name](...args);
        setPreviews((state) => {const next = {...state}; delete next[key]; return next;});
      }
      if (name === "editUploadImages" && value) {
        const slot = imageSlotForKey(String(args[0]), current.current.instanceId) ?? "main";
        setVersions((state) => ({...state, [slot]: (state[slot] ?? 0) + 1}));
      }
    };
    const imageKey = (args: unknown[]) => `image:${imageSlotForKey(String(args[0]), current.current.instanceId) ?? "main"}`;
    return {
      ...writers,
      uploadImageFile: (file: File, key: string, isLogo = false) => upload(isLogo ? "logo" : (imageSlotForKey(key, current.current.instanceId) ?? "image") as AssetTarget, file),
      cancelImageUpload: (key: string, isLogo = false) => cancelUpload(isLogo ? "logo" : (imageSlotForKey(key, current.current.instanceId) ?? "image") as AssetTarget),
      editUploadImages: assetWriter("editUploadImages", 1, imageKey),
      removeUploadImages: (key: string) => {
        cancelUpload((imageSlotForKey(key, current.current.instanceId) ?? "image") as AssetTarget);
        writers.removeUploadImages(key);
        setPreviews((state) => {const next = {...state}; delete next[imageKey([key])]; return next;});
      },
      editLogos: assetWriter("editLogos", 1, () => "logo"),
      editCustomBg: assetWriter("editCustomBg", 0, () => "background"),
      editCustomBgs: assetWriter("editCustomBgs", 1, () => "background"),
      incPendingUpload: () => {if (alive.current) setPending((n) => n + 1);},
      decPendingUpload: () => {if (alive.current) setPending((n) => Math.max(0, n - 1));},
      editIsBgAnimation: (value: boolean) => {if (alive.current) setAnimation((a) => a.background === value ? a : {...a, background: value});},
      editIsUploadAnimation: (value: boolean) => {if (alive.current) setAnimation((a) => a.upload === value ? a : {...a, upload: value});},
    };
  }, [writers, upload, cancelUpload]);
  const scene = useMemo(() => bannerToLegacyScene(banner, banner.instanceId), [banner]);
  const previewScene = {...scene} as Record<string, any>;
  previewScene.uploadImages = {...scene.uploadImages as object};
  if (previews["image:main"]) previewScene.uploadImages = {...previewScene.uploadImages, main: previews["image:main"], [banner.instanceId]: previews["image:main"]};
  for (const [key, url] of Object.entries(previews)) {
    if (!key.startsWith("image:") || key === "image:main") continue;
    const slot = key.slice(6);
    const imageKey = /^(speaker-|ls-speaker-|custom-qr-)/.test(slot) ? slot : `${slot}-${banner.instanceId}`;
    previewScene.uploadImages[imageKey] = url;
  }
  if (previews.logo) previewScene.logos = {main: previews.logo, [banner.instanceId]: previews.logo};
  if (previews.background) {
    previewScene.customBg = previews.background;
    previewScene.customBgs = {main: previews.background, [banner.instanceId]: previews.background};
  }
  const settings = {
    ...INITIAL_SETTING_STATE, ...scene, ...writers,
    editLayoutOption: selectLayout,
    dataChannel: String((scene.channel as {value: string}).value), dataType: banner.contentType, dataChannelObj: scene.channel, dataTypeObj: scene.type,
    sceneSet: banner.set, isControlledBanner: true,
    registerGenerativeFrameReader, getGenerativeFrame: () => frameReader.current?.() ?? null,
    bgOption: previews.background || (banner.appearance.backgroundOption !== "generative" && banner.assets.background !== null) ? "custom" : banner.appearance.backgroundOption,
    hash: banner.instanceId, isAnimationPaused: tools.paused, isDropzoneVisible: tools.dropzones, isTypografEnabled: tools.typograf,
    isBgToolsExpanded: {[banner.instanceId]: tools.background}, isImageToolsExpanded: tools.image, currentImageToolsId: tools.imageId,
    editIsBgToolsExpanded: (_id: string, value: boolean) => setTools((t) => ({...t, background: Boolean(_id && value)})),
    editIsImageToolsExpanded: (value: boolean) => setTools((t) => ({...t, image: value, ...(value ? {background: false} : {})})),
    editCurrentImageToolsId: (imageId: string | null) => setTools((t) => ({...t, imageId})),
    editIsDropzoneVisible: (dropzones: boolean) => setTools((t) => ({...t, dropzones})),
    editIsTypografEnabled: (typograf: boolean) => setTools((t) => ({...t, typograf})),
    toggleAnimationPaused: () => setTools((t) => ({...t, paused: !t.paused})),
  };
  const edits = {...INITIAL_EDITS_STATE, ...scene, ...writers};
  const images = {...INITIAL_IMAGES_STATE, ...previewScene, ...imageWriters, pendingUploadCount: pending,
    getUploadImageVersion: (key: string) => versions[imageSlotForKey(key, banner.instanceId) ?? "main"] ?? 0,
    isBgAnimation: animation.background, isUploadAnimation: animation.upload};
  return (
    <Setting.Provider value={settings as unknown as React.ContextType<typeof Setting>}>
      <Edits.Provider value={edits as unknown as React.ContextType<typeof Edits>}>
        <Images.Provider value={images as unknown as React.ContextType<typeof Images>}>
          <BannerRuntime.Provider value={{upload, cancelUpload, selectBackground, layoutOption: visualSelection?.layout ?? null,
            backgroundOption: visualSelection?.background ?? null, pending, error: uploadError}}>{children}</BannerRuntime.Provider>
        </Images.Provider>
      </Edits.Provider>
    </Setting.Provider>
  );
}
